import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Play, Lock, ChevronLeft, ChevronRight, Download, X, ArrowLeft, LogOut, Film, Share2, Copy, Check, ShieldAlert, Heart, MessageSquarePlus, Send, Plus, Minus, MessageSquare, Sparkles, Zap, AlertCircle, ExternalLink, Smartphone, Globe, CheckCircle2, HelpCircle } from 'lucide-react';
import { ClientDelivery, AppConfig, VideoItem, GuestComment, DownloadLog } from '../types';
import { themeStyles } from '../App';
import { checkRateLimit, recordFailedAttempt, resetRateLimit } from '../utils/rateLimiter';
import { detectDevice } from '../utils/deviceDetector';
import { db, doc, setDoc } from '../lib/firebase';

interface ClientDeliveryViewProps {
  delivery: ClientDelivery;
  config: AppConfig;
  isStandalone?: boolean;
  isShareMode?: boolean;
  onClose?: () => void;
  onLogout?: () => void;
  onAddComment?: (deliveryId: string, comment: GuestComment) => Promise<void>;
}

export function ClientDeliveryView({ delivery, config, isStandalone, isShareMode = false, onClose, onLogout, onAddComment }: ClientDeliveryViewProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (isStandalone || isShareMode || !delivery.password) return true;
    return sessionStorage.getItem(`lc_auth_${delivery.id}`) === 'true';
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [error, setError] = useState(false);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [useLightQuality, setUseLightQuality] = useState(() => {
    if (typeof window === 'undefined') return false;
    const isSmallScreen = window.innerWidth <= 768;
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const connection = (navigator as Record<string, any>).connection;
    const isSlowConn = connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType);
    return isSmallScreen || isMobileUA || Boolean(isSlowConn);
  });

  // Lock body scroll while Client Delivery View is active
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Sync state if session storage or props change
  React.useEffect(() => {
    if (isStandalone || isShareMode || !delivery.password) {
      setIsAuthenticated(true);
    } else if (sessionStorage.getItem(`lc_auth_${delivery.id}`) === 'true') {
      setIsAuthenticated(true);
    }
  }, [delivery.id, delivery.password, isStandalone, isShareMode]);

  // Guestbook state
  const [guestName, setGuestName] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [localComments, setLocalComments] = useState<GuestComment[]>(delivery.comments || []);
  const [isCommentsOverlayOpen, setIsCommentsOverlayOpen] = useState(false);
  const [showOverlayForm, setShowOverlayForm] = useState(false);
  const [tickerIndex, setTickerIndex] = useState(0);

  React.useEffect(() => {
    if (localComments.length <= 1) return;
    const timer = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % localComments.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [localComments.length]);

  const activeTheme = config.theme || 'cosmic-charcoal';
  const thumbnailScrollRef = React.useRef<HTMLDivElement>(null);

  const scrollThumbnails = (direction: 'left' | 'right') => {
    if (thumbnailScrollRef.current) {
      const scrollAmount = direction === 'left' ? -240 : 240;
      thumbnailScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Consolidate video items
  const videoList: VideoItem[] = useMemo(() => {
    if (delivery.videos && delivery.videos.length > 0) {
      return delivery.videos;
    }
    if (delivery.videoUrl) {
      return [{
        id: 'v-main',
        title: 'Film de Mariage',
        videoUrl: delivery.videoUrl
      }];
    }
    return [];
  }, [delivery]);

  const activeVideo = videoList[selectedVideoIndex] || videoList[0];

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();

    // Check rate limit (e.g., max 5 attempts per minute per delivery)
    const limitCheck = checkRateLimit(`delivery_${delivery.id}`, 5, 60000, 60000);
    if (!limitCheck.allowed) {
      setRateLimitMessage(`Trop de tentatives. Veuillez patienter ${limitCheck.remainingSeconds} secondes.`);
      return;
    }

    const cleanInput = passwordInput.trim().toLowerCase();
    const cleanPassword = (delivery.password || '').trim().toLowerCase();
    const cleanId = (delivery.id || '').trim().toLowerCase();

    if (cleanInput && (cleanInput === cleanPassword || cleanInput === cleanId)) {
      resetRateLimit(`delivery_${delivery.id}`);
      setIsAuthenticated(true);
      sessionStorage.setItem(`lc_auth_${delivery.id}`, 'true');
      setError(false);
      setRateLimitMessage(null);
    } else {
      const failed = recordFailedAttempt(`delivery_${delivery.id}`, 5, 60000, 60000);
      if (failed.blocked) {
        setRateLimitMessage(`5 tentatives échouées. Compte temporairement bloqué pendant ${failed.remainingSeconds}s.`);
      } else {
        setError(true);
        setRateLimitMessage(null);
      }
    }
  };

  const getGuestShareUrl = () => {
    const origin = window.location.origin;
    const vidParam = activeVideo ? `&v=${selectedVideoIndex}` : '';
    return `${origin}/?delivery=${delivery.id}&mode=guest${vidParam}`;
  };

  const handleCopyShareLink = () => {
    const link = getGuestShareUrl();
    navigator.clipboard.writeText(link);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 3000);
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim() || !guestMessage.trim()) return;

    setIsSubmittingComment(true);
    const newComment: GuestComment = {
      id: 'c-' + Date.now() + '-' + Math.floor(Math.random() * 1000),
      authorName: guestName.trim(),
      message: guestMessage.trim(),
      createdAt: new Date().toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    const updated = [newComment, ...localComments];
    setLocalComments(updated);

    if (onAddComment) {
      await onAddComment(delivery.id, newComment);
    }

    setGuestName('');
    setGuestMessage('');
    setIsSubmittingComment(false);
    setCommentSubmitted(true);
    setTimeout(() => setCommentSubmitted(false), 4000);
  };

  // Download assistance & telemetry state
  const [showDownloadHelpModal, setShowDownloadHelpModal] = useState(false);
  const [downloadModalVideo, setDownloadModalVideo] = useState<VideoItem | null>(null);
  const [downloadFeedbackToast, setDownloadFeedbackToast] = useState<string | null>(null);
  const [copiedDirectDownloadUrl, setCopiedDirectDownloadUrl] = useState(false);
  const [detectedInAppInfo, setDetectedInAppInfo] = useState<ReturnType<typeof detectDevice> | null>(null);

  // Helper for resilient stream download with HTTP Range / 206 Partial Content support
  const getResilientDownloadUrl = (rawUrl: string, title?: string) => {
    if (!rawUrl) return '';
    const cleanFilename = `${(delivery.clientName || 'Film').replace(/[^a-zA-Z0-9_-]/g, '_')}_${(title || 'Film_Mariage').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) {
      return `/api/download-stream?url=${encodeURIComponent(rawUrl)}&filename=${encodeURIComponent(cleanFilename)}`;
    }
    return rawUrl;
  };

  // Send download log to Firestore and Express server
  const sendDownloadLog = async (
    video: VideoItem,
    status: 'initiated' | 'completed' | 'in-app-warning' | 'error',
    errorMessage?: string,
    fileTypeOverride?: DownloadLog['fileType']
  ) => {
    try {
      const dev = detectDevice();
      setDetectedInAppInfo(dev);
      const targetUrl = video.downloadUrl || video.videoUrl || '';
      const logId = 'dl-' + Date.now() + '-' + Math.random().toString(36).substring(2, 8);
      
      const fileType: DownloadLog['fileType'] = fileTypeOverride || (video.downloadUrl ? 'video-master' : 'video-hd');

      const logEntry: DownloadLog = {
        id: logId,
        timestamp: new Date().toISOString(),
        deliveryId: delivery.id,
        clientName: delivery.clientName || 'Client Inconnu',
        videoTitle: video.title || 'Film de Mariage',
        fileType,
        fileUrl: targetUrl,
        deviceType: dev.deviceType,
        os: dev.os,
        browser: dev.browser,
        isInAppBrowser: dev.isInAppBrowser,
        inAppName: dev.inAppName,
        language: dev.language,
        timeZone: dev.timeZone,
        countryGuess: dev.countryGuess,
        screenResolution: dev.screenResolution,
        connectionType: dev.connectionType,
        status,
        errorMessage,
        userAgent: dev.userAgent
      };

      // 1. Firebase Firestore sync
      try {
        await setDoc(doc(db, "download_logs", logId), logEntry, { merge: true });
      } catch (fbErr) {
        console.warn("Firebase download log sync warning:", fbErr);
      }

      // 2. Express Server sync
      try {
        await fetch("/api/download-logs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(logEntry)
        });
      } catch (srvErr) {
        console.warn("Server download log sync warning:", srvErr);
      }
    } catch (err) {
      console.error("sendDownloadLog error:", err);
    }
  };

  // Automatically record access log when client enters private delivery
  useEffect(() => {
    if (isAuthenticated || isShareMode) {
      const sessionLoggedKey = `lc_access_logged_${delivery.id}`;
      if (!sessionStorage.getItem(sessionLoggedKey)) {
        sessionStorage.setItem(sessionLoggedKey, 'true');
        const v = activeVideo || videoList[0] || { id: 'main', title: 'Galerie Privée', videoUrl: delivery.videoUrl || '' };
        sendDownloadLog(v, 'completed', 'Connexion / Accès à la galerie privée réussi', 'gallery-access');
      }
    }
  }, [isAuthenticated, isShareMode, delivery.id]);

  const handleDownloadClick = async (e: React.MouseEvent, video: VideoItem) => {
    e.preventDefault();
    const dev = detectDevice();
    setDetectedInAppInfo(dev);
    setDownloadModalVideo(video);

    // If client is viewing inside an in-app browser (e.g. LINE, Instagram, Facebook),
    // opening direct download will almost certainly produce a blank page or get blocked!
    if (dev.isInAppBrowser) {
      await sendDownloadLog(video, 'in-app-warning', `Navigateur intégré détecté: ${dev.inAppName || 'WebView'}`);
      setShowDownloadHelpModal(true);
      return;
    }

    // Standard browser: log and trigger download
    await sendDownloadLog(video, 'initiated');

    const rawDownloadUrl = video.downloadUrl || video.videoUrl;
    if (!rawDownloadUrl) return;

    const streamUrl = getResilientDownloadUrl(rawDownloadUrl, video.title);
    const cleanFilename = `${(delivery.clientName || 'Film').replace(/[^a-zA-Z0-9_-]/g, '_')}_${(video.title || 'Film_Mariage').replace(/[^a-zA-Z0-9_-]/g, '_')}.mp4`;

    // Trigger download gracefully using the anti-drop stream proxy
    try {
      const a = document.createElement('a');
      a.href = streamUrl;
      a.download = cleanFilename;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setDownloadFeedbackToast('Téléchargement haute vitesse lancé !');
      setTimeout(() => setDownloadFeedbackToast(null), 4000);
      await sendDownloadLog(video, 'completed');
    } catch (err: any) {
      console.error("Direct download trigger error:", err);
      await sendDownloadLog(video, 'error', err?.message || 'Download trigger failed');
      setShowDownloadHelpModal(true);
    }
  };

  return (
    <div className="bg-theme-bg text-theme-text min-h-screen relative font-sans selection:bg-theme-accent selection:text-theme-bg flex flex-col">
      <style dangerouslySetInnerHTML={{ __html: themeStyles[activeTheme] }} />

      {/* Simplified Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-theme-bg/95 backdrop-blur-md border-b border-theme-border px-3 py-2.5 sm:px-6 sm:py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 sm:gap-3 z-10">
          <button 
            onClick={() => {
              if (onClose) {
                onClose();
              } else {
                window.location.href = window.location.origin + window.location.pathname;
              }
            }} 
            className="text-theme-secondary hover:text-theme-text font-mono text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer bg-theme-card/80 px-2.5 py-1.5 border border-theme-border hover:border-theme-accent transition-colors rounded"
          >
            <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
            <span className="hidden sm:inline">Accueil</span>
            <span className="sm:hidden">Retour</span>
          </button>

          {onLogout && !isShareMode && (
            <button 
              onClick={() => {
                sessionStorage.removeItem(`lc_auth_${delivery.id}`);
                onLogout();
              }} 
              className="text-red-400 hover:text-red-300 font-mono text-xs uppercase tracking-wider flex items-center gap-1.5 cursor-pointer bg-red-500/10 hover:bg-red-500/20 px-2.5 py-1.5 border border-red-500/20 rounded transition-colors"
              title="Déconnexion espace client"
            >
              <LogOut className="w-3.5 h-3.5 shrink-0 text-red-400" />
              <span className="hidden sm:inline">Déconnexion</span>
            </button>
          )}
        </div>

        {/* Studio Brand/Logo: hidden on mobile (<640px) to prevent ANY overlap with buttons */}
        <div className="hidden sm:block absolute left-1/2 -translate-x-1/2 max-w-[160px] md:max-w-xs text-center px-2 pointer-events-none">
          {config.logoUrl ? (
            <img src={config.logoUrl} alt={config.studioName} className="h-6 md:h-8 max-w-full object-contain mx-auto" />
          ) : (
            <span className="text-xs sm:text-sm md:text-base font-serif tracking-wider text-theme-text font-semibold truncate block">
              {config.studioName}
            </span>
          )}
        </div>
        
        <button
          onClick={() => {
            if (onClose) {
              onClose();
            } else {
              window.location.href = window.location.origin + window.location.pathname;
            }
          }}
          aria-label="Fermer et retourner au site"
          title="Fermer et retourner au site"
          className="p-1.5 sm:p-2 text-theme-secondary hover:text-theme-text hover:bg-theme-card border border-theme-border rounded-full transition-all cursor-pointer flex items-center justify-center bg-theme-bg z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-12 pt-28 md:pt-36 w-full max-w-6xl mx-auto">
        {!isAuthenticated ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md bg-theme-card border border-theme-border p-8 md:p-12 text-center"
          >
            <Lock className="w-8 h-8 text-theme-accent mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl font-serif text-theme-text mb-2">Espace Privé</h2>
            <p className="text-theme-secondary mb-8 text-sm">Veuillez entrer le mot de passe pour accéder à vos films.</p>
            
            <form onSubmit={handleLogin} className="flex flex-col gap-4">
              <input 
                type="password"
                placeholder="Mot de passe"
                value={passwordInput}
                onChange={(e) => {
                  setPasswordInput(e.target.value);
                  setError(false);
                  setRateLimitMessage(null);
                }}
                className="w-full bg-theme-bg border border-theme-border text-center text-sm px-4 py-4 text-theme-text outline-none focus:border-theme-accent font-mono transition-colors"
              />
              {error && <p className="text-red-500 text-xs font-mono">Mot de passe incorrect</p>}
              {rateLimitMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-mono flex items-center justify-center gap-2">
                  <ShieldAlert className="w-4 h-4 shrink-0" />
                  <span>{rateLimitMessage}</span>
                </div>
              )}
              <button 
                type="submit"
                className="w-full bg-theme-accent text-theme-bg px-6 py-4 uppercase tracking-[0.2em] text-xs font-semibold hover:opacity-90 transition-opacity"
              >
                Accéder aux films
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full flex flex-col gap-8 md:gap-12"
          >
            <div className="text-center space-y-4">
              <h1 className="text-4xl md:text-6xl font-serif text-theme-text">{delivery.clientName}</h1>
              {delivery.date && delivery.date.trim() !== '' && (
                <p className="font-mono text-sm tracking-widest uppercase text-theme-secondary">{delivery.date}</p>
              )}
            </div>

            {delivery.message && (
              <div className="max-w-2xl mx-auto text-center">
                <p className="text-theme-secondary leading-relaxed italic">« {delivery.message} »</p>
              </div>
            )}

            {/* Selected Video Title & Description + 4G Mobile Toggle Bar */}
            {activeVideo && (
              <div className="text-center space-y-2">
                <h3 className="text-2xl md:text-3xl font-serif text-theme-text">{activeVideo.title}</h3>
                {activeVideo.description && (
                  <p className="text-xs font-mono text-theme-secondary max-w-xl mx-auto">{activeVideo.description}</p>
                )}

                {/* 4G / Mobile Quality Switcher Bar if lightVideoUrl is provided */}
                {activeVideo.lightVideoUrl && (
                  <div className="pt-1 flex flex-wrap items-center justify-center gap-3">
                    <div className="inline-flex items-center gap-1.5 bg-black/80 border border-amber-500/30 p-1.5 px-3 rounded-full text-xs font-mono shadow-xl backdrop-blur-md">
                      <Zap className="w-3.5 h-3.5 text-amber-400 animate-pulse shrink-0" />
                      <span className="text-amber-300 font-bold hidden xs:inline">Qualité :</span>
                      <div className="flex items-center gap-1 bg-zinc-900/90 p-0.5 rounded-full border border-white/10">
                        <button
                          onClick={() => setUseLightQuality(false)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            !useLightQuality
                              ? 'bg-purple-600 text-white font-bold shadow-md'
                              : 'text-zinc-400 hover:text-white'
                          }`}
                        >
                          🎬 Master HD (PC / Mac)
                        </button>
                        <button
                          onClick={() => setUseLightQuality(true)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all flex items-center gap-1 ${
                            useLightQuality
                              ? 'bg-amber-500 text-black font-bold shadow-md'
                              : 'text-amber-300 hover:text-amber-200'
                          }`}
                        >
                          <Zap className="w-3 h-3" />
                          <span>⚡ 4G Mobile</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Video Player + Floating Comments Overlay & Live Ticker */}
            {activeVideo ? (
              <div className="relative w-full aspect-[4/3] sm:aspect-video bg-black border border-theme-border overflow-hidden group shadow-2xl flex flex-col justify-center">
                {/* Inner 16:9 Video & Cover Frame */}
                <div className="relative w-full aspect-video my-auto overflow-hidden">
                  {!isPlaying ? (
                    <>
                      {(activeVideo.thumbnailUrl || delivery.coverUrl) ? (
                        <img 
                          src={activeVideo.thumbnailUrl || delivery.coverUrl} 
                          alt={activeVideo.title || delivery.clientName} 
                          className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity duration-700" 
                        />
                      ) : (
                        <div className="w-full h-full bg-zinc-900/50" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
                        <button 
                          onClick={() => setIsPlaying(true)}
                          className="w-16 h-16 md:w-24 md:h-24 rounded-full border border-theme-accent/50 text-theme-accent flex items-center justify-center bg-theme-bg/30 backdrop-blur-md hover:scale-110 hover:bg-theme-accent hover:text-theme-bg transition-all duration-500 cursor-pointer shadow-2xl"
                          aria-label="Lancer la vidéo"
                        >
                          <Play className="w-7 h-7 ml-1 md:w-8 md:h-8 md:ml-2" />
                        </button>
                        <span className="text-xs font-mono uppercase tracking-widest bg-theme-bg/80 px-3 py-1 border border-theme-border text-theme-text backdrop-blur-sm">
                          {activeVideo.title || 'Lancer le film'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <video 
                      key={useLightQuality ? (activeVideo.lightVideoUrl || activeVideo.videoUrl) : activeVideo.videoUrl}
                      controls 
                      autoPlay 
                      playsInline
                      preload="metadata"
                      className="w-full h-full object-contain bg-black"
                    >
                      {activeVideo.lightVideoUrl && (
                        <source 
                          src={useLightQuality ? activeVideo.lightVideoUrl : activeVideo.videoUrl} 
                          type="video/mp4" 
                        />
                      )}
                      <source src={activeVideo.videoUrl} type="video/mp4" />
                      Votre navigateur ne prend pas en charge la lecture vidéo HTML5.
                    </video>
                  )}
                </div>

                {/* Floating Badge Toggle (+ / -) for Comments Overlay (top-right corner) */}
                <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 z-30 flex items-center gap-2">
                  <button
                    onClick={() => {
                      setIsCommentsOverlayOpen(!isCommentsOverlayOpen);
                      if (!isCommentsOverlayOpen && localComments.length === 0) {
                        setShowOverlayForm(true);
                      }
                    }}
                    className={`px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-full backdrop-blur-md border text-xs font-mono transition-all duration-300 shadow-xl cursor-pointer flex items-center gap-1.5 sm:gap-2 ${
                      isCommentsOverlayOpen
                        ? 'bg-theme-accent text-theme-bg border-theme-accent font-bold scale-105'
                        : 'bg-black/85 hover:bg-black text-white border-white/20 hover:border-theme-accent'
                    }`}
                    title={isCommentsOverlayOpen ? "Ranger les mots doux" : "Réagir / Laissez un mot doux"}
                  >
                    <Heart className={`w-3.5 h-3.5 ${isCommentsOverlayOpen ? 'fill-theme-bg' : 'text-pink-400 fill-pink-400'}`} />
                    <span className="text-[11px] sm:text-xs font-sans font-medium">Réagir</span>
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px] font-bold font-mono">
                      {localComments.length}
                    </span>
                    <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold font-mono">
                      {isCommentsOverlayOpen ? '−' : '+'}
                    </span>
                  </button>
                </div>

                {/* Live Stream Style Floating Ticker (When overlay is collapsed) */}
                {!isCommentsOverlayOpen && localComments.length > 0 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={tickerIndex}
                      initial={{ opacity: 0, y: -10, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.96 }}
                      transition={{ duration: 0.35 }}
                      onClick={() => setIsCommentsOverlayOpen(true)}
                      className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 max-w-[calc(100%-140px)] sm:max-w-md z-20 bg-black/85 hover:bg-black backdrop-blur-md border border-purple-500/40 text-white p-1.5 px-2.5 sm:p-2 sm:px-3 rounded-lg sm:rounded-xl shadow-2xl cursor-pointer flex items-center justify-between gap-2 text-[11px] sm:text-xs group transition-all"
                    >
                      <div className="flex items-center gap-2 overflow-hidden">
                        <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5 shrink-0">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-pink-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2 w-2 sm:h-2.5 sm:w-2.5 bg-pink-500"></span>
                        </span>
                        <div className="truncate text-left leading-tight">
                          <span className="font-bold text-purple-300 font-serif mr-1">
                            {localComments[tickerIndex % localComments.length]?.authorName} :
                          </span>
                          <span className="italic text-zinc-200 font-sans">
                            "{localComments[tickerIndex % localComments.length]?.message}"
                          </span>
                        </div>
                      </div>
                      <span className="text-[9px] sm:text-[10px] font-mono uppercase tracking-wider text-purple-300 group-hover:text-white shrink-0 bg-purple-500/30 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded border border-purple-500/40 flex items-center gap-1 font-semibold hidden xs:flex">
                        <span>+ Réagir</span>
                      </span>
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Live Comments Overlay Drawer (TikTok / YouTube Live Chat Overlay Style) */}
                <AnimatePresence>
                  {isCommentsOverlayOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: '100%' }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: '100%' }}
                      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                      className="absolute inset-0 z-30 bg-black/90 backdrop-blur-md p-4 flex flex-col justify-between text-left overflow-hidden border border-purple-500/40"
                    >
                      {/* Header Overlay */}
                      <div className="flex items-center justify-between border-b border-white/10 pb-3 shrink-0">
                        <div className="flex items-center gap-2 text-purple-300">
                          <Heart className="w-4 h-4 fill-pink-500 text-pink-500" />
                          <h4 className="text-sm font-serif font-bold text-white">Mots doux & Réactions ({localComments.length})</h4>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowOverlayForm(!showOverlayForm)}
                            className="px-2.5 py-1 bg-purple-600 hover:bg-purple-500 text-white text-[11px] font-mono uppercase tracking-wider rounded flex items-center gap-1 transition-colors cursor-pointer font-bold"
                          >
                            {showOverlayForm ? 'Masquer formulaire' : '+ Écrire un mot'}
                          </button>
                          <button
                            onClick={() => setIsCommentsOverlayOpen(false)}
                            className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors cursor-pointer font-bold font-mono text-sm"
                            title="Ranger le volet"
                          >
                            −
                          </button>
                        </div>
                      </div>

                      {/* Body Content */}
                      <div className="flex-1 overflow-y-auto scrollbar-thin py-3 space-y-3 pr-1">
                        {showOverlayForm ? (
                          <form 
                            onSubmit={async (e) => {
                              await handleAddComment(e);
                              setShowOverlayForm(false);
                            }} 
                            className="bg-purple-950/40 border border-purple-500/30 p-3 rounded-lg space-y-3 shadow-inner"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-mono text-purple-300 uppercase tracking-wider font-bold">Publier un message en direct :</span>
                            </div>
                            <div>
                              <input
                                type="text"
                                required
                                placeholder="Votre Prénom / Nom / Famille..."
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                                className="w-full bg-black/60 border border-white/20 p-2 text-xs text-white outline-none focus:border-purple-400 font-sans rounded"
                              />
                            </div>
                            <div>
                              <textarea
                                required
                                rows={2}
                                placeholder="Superbe vidéo, quelles émotions !..."
                                value={guestMessage}
                                onChange={(e) => setGuestMessage(e.target.value)}
                                className="w-full bg-black/60 border border-white/20 p-2 text-xs text-white outline-none focus:border-purple-400 font-sans rounded resize-none"
                              />
                            </div>
                            <button
                              type="submit"
                              disabled={isSubmittingComment}
                              className="w-full py-2 bg-purple-600 hover:bg-purple-500 text-white font-mono text-xs uppercase tracking-wider font-bold rounded flex items-center justify-center gap-2 transition-colors cursor-pointer"
                            >
                              <Send className="w-3.5 h-3.5" />
                              <span>{isSubmittingComment ? 'Publication...' : 'Publier en direct'}</span>
                            </button>
                          </form>
                        ) : null}

                        {localComments.length === 0 ? (
                          <div className="text-center py-8 space-y-3">
                            <p className="text-xs text-zinc-400 font-mono">Aucun mot doux pour l'instant.</p>
                            <button
                              onClick={() => setShowOverlayForm(true)}
                              className="px-4 py-2 bg-purple-600 text-white text-xs font-mono uppercase tracking-wider rounded font-bold cursor-pointer"
                            >
                              Soyez le premier à réagir !
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {localComments.map((comment) => (
                              <div key={comment.id} className="p-2.5 bg-white/5 border border-white/10 rounded-lg space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-serif font-bold text-purple-300">{comment.authorName}</span>
                                  <span className="text-[9px] font-mono text-zinc-400">{comment.createdAt}</span>
                                </div>
                                <p className="text-xs text-zinc-200 font-sans italic leading-relaxed">
                                  "{comment.message}"
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Footer note inside Overlay */}
                      <div className="pt-2 border-t border-white/10 text-center shrink-0 flex items-center justify-between text-[10px] font-mono text-zinc-400">
                        <span>{localComments.length} mot(s) publié(s)</span>
                        <button 
                          onClick={() => {
                            setIsCommentsOverlayOpen(false);
                            const el = document.getElementById('espace-mots-doux');
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }} 
                          className="text-purple-300 hover:underline cursor-pointer"
                        >
                          Voir la section complète ↓
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="p-12 text-center border border-theme-border text-theme-secondary font-mono text-xs">
                Aucune vidéo disponible pour cette livraison.
              </div>
            )}

            {/* Multi-video Vignettes Carousel under player */}
            {videoList.length > 1 && (
              <div className="w-full space-y-3 pt-6 border-t border-theme-border/60">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="text-xs font-mono uppercase tracking-widest text-theme-text font-bold flex items-center gap-2">
                      <Film className="w-4 h-4 text-theme-accent" />
                      <span>Autres vidéos ({videoList.length})</span>
                    </h3>
                    <p className="text-[11px] font-mono text-theme-secondary hidden sm:block">
                      Faites défiler pour choisir une vidéo
                    </p>
                  </div>

                  {/* Nav Arrows */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => scrollThumbnails('left')}
                      className="p-1.5 bg-theme-card hover:bg-theme-accent hover:text-theme-bg border border-theme-border transition-colors cursor-pointer rounded text-theme-text"
                      aria-label="Défiler vers la gauche"
                      title="Précédent"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => scrollThumbnails('right')}
                      className="p-1.5 bg-theme-card hover:bg-theme-accent hover:text-theme-bg border border-theme-border transition-colors cursor-pointer rounded text-theme-text"
                      aria-label="Défiler vers la droite"
                      title="Suivant"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Horizontal Scrollable Carousel */}
                <div className="relative w-full">
                  <div 
                    ref={thumbnailScrollRef}
                    className="flex items-stretch gap-3 overflow-x-auto scrollbar-none py-2 px-0.5 scroll-smooth"
                  >
                    {videoList.map((v, idx) => {
                      const isSelected = selectedVideoIndex === idx;
                      const vCover = v.thumbnailUrl || delivery.coverUrl;

                      return (
                        <button
                          key={v.id || idx}
                          onClick={() => {
                            setSelectedVideoIndex(idx);
                            setIsPlaying(false);
                          }}
                          className={`group text-left overflow-hidden border transition-all duration-300 cursor-pointer flex flex-col shrink-0 w-44 sm:w-56 md:w-64 ${
                            isSelected
                              ? 'border-theme-accent bg-theme-card ring-2 ring-theme-accent/40 shadow-xl scale-[1.01]'
                              : 'border-theme-border bg-theme-card/40 hover:bg-theme-card hover:border-theme-accent/60'
                          }`}
                        >
                          {/* Thumbnail Preview */}
                          <div className="relative aspect-video w-full bg-black/60 overflow-hidden shrink-0">
                            {vCover ? (
                              <img 
                                src={vCover} 
                                alt={v.title} 
                                className={`w-full h-full object-cover transition-transform duration-500 ${
                                  isSelected ? 'scale-105 opacity-90' : 'opacity-70 group-hover:opacity-95 group-hover:scale-105'
                                }`}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-zinc-600">
                                <Film className="w-6 h-6" />
                              </div>
                            )}

                            {/* Status Badge */}
                            <div className="absolute top-1.5 left-1.5 z-10">
                              {isSelected ? (
                                <span className="bg-theme-accent text-theme-bg font-mono text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 shadow-md flex items-center gap-1">
                                  <Play className="w-2.5 h-2.5 fill-current" />
                                  En lecture
                                </span>
                              ) : (
                                <span className="bg-black/80 text-white/90 backdrop-blur-sm font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5">
                                  #{idx + 1}
                                </span>
                              )}
                            </div>

                            {/* Play overlay */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/10 transition-colors">
                              <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all ${
                                isSelected ? 'bg-theme-accent text-theme-bg scale-110 shadow-lg' : 'bg-black/60 text-white group-hover:bg-theme-accent group-hover:text-theme-bg group-hover:scale-110'
                              }`}>
                                <Play className="w-3 h-3 ml-0.5 fill-current" />
                              </div>
                            </div>
                          </div>

                          {/* Title & Description */}
                          <div className="p-2.5 space-y-0.5 flex-1 flex flex-col justify-between">
                            <h4 className={`text-xs sm:text-sm font-serif line-clamp-1 ${isSelected ? 'text-theme-accent font-semibold' : 'text-theme-text'}`}>
                              {v.title || `Vidéo #${idx + 1}`}
                            </h4>
                            {v.description && (
                              <p className="text-[10px] sm:text-[11px] text-theme-secondary line-clamp-1 font-mono">
                                {v.description}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Download and Share section */}
            {activeVideo && (
              <div className="flex flex-col items-center justify-center gap-4 max-w-2xl mx-auto px-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
                  {!isShareMode && (
                    <button
                      type="button"
                      onClick={(e) => handleDownloadClick(e, activeVideo)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-all font-mono text-xs uppercase tracking-widest cursor-pointer shadow-lg text-center rounded group"
                      title="Télécharger la version Ultra Master (meilleure qualité possible)"
                    >
                      <Download className="w-4 h-4 shrink-0 text-emerald-200 group-hover:scale-110 transition-transform" />
                      <div className="flex flex-col text-left">
                        <span>Télécharger {videoList.length > 1 ? `"${activeVideo.title}"` : 'le film'}</span>
                        <span className="text-[10px] text-emerald-200 font-sans normal-case tracking-normal opacity-90">
                          {activeVideo.downloadUrl ? '💎 Version Ultra Master Brut' : '💎 Version Haute Qualité HD'}
                        </span>
                      </div>
                    </button>
                  )}

                  {!isShareMode && (
                    <button
                      type="button"
                      onClick={() => {
                        setDownloadModalVideo(activeVideo);
                        setShowDownloadHelpModal(true);
                      }}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-4 bg-theme-card border border-theme-border text-theme-secondary hover:text-theme-text hover:border-theme-accent transition-colors font-mono text-xs uppercase tracking-wider cursor-pointer rounded"
                      title="Options de téléchargement & liens directs"
                    >
                      <HelpCircle className="w-4 h-4 text-theme-accent shrink-0" />
                      <span>Aide & Liens directs</span>
                    </button>
                  )}

                  {!isShareMode && (
                    <button
                      type="button"
                      onClick={() => setShowShareModal(true)}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-theme-card border border-theme-border text-theme-text hover:border-theme-accent transition-colors font-mono text-xs uppercase tracking-widest cursor-pointer text-center rounded"
                    >
                      <Share2 className="w-4 h-4 shrink-0 text-theme-accent" />
                      <span>Partager aux proches</span>
                    </button>
                  )}
                </div>

                {/* Toast Feedback for Download Trigger */}
                <AnimatePresence>
                  {downloadFeedbackToast && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="p-3 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-mono flex items-center gap-2 text-center rounded shadow-lg"
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span>{downloadFeedbackToast}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isShareMode && (
                  <div className="p-4 bg-theme-card border border-theme-border text-center space-y-1">
                    <p className="text-xs font-mono text-theme-secondary uppercase tracking-widest text-theme-accent font-semibold">Mode Visionnage Proches</p>
                    <p className="text-xs text-theme-secondary">Lien de lecture directe partagé. Le téléchargement et l'accès au compte client sont désactivés.</p>
                  </div>
                )}

                {onLogout && !isShareMode && (
                  <button 
                    onClick={onLogout}
                    className="inline-flex items-center gap-2 text-xs font-mono text-theme-secondary hover:text-red-400 transition-colors uppercase tracking-wider cursor-pointer mt-2"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    <span>Se déconnecter de la session</span>
                  </button>
                )}

                {!isShareMode && (
                  <p className="text-[11px] text-theme-secondary text-center font-mono opacity-80 mt-2">
                    💡 <span className="underline">Astuce</span> : Si la vidéo s'ouvre dans le lecteur au lieu de s'enregistrer, faites un clic droit ou appui long puis <strong className="text-theme-text font-semibold">"Enregistrer la vidéo sous..."</strong> ou cliquez sur <strong>"Aide & Liens directs"</strong>.
                  </p>
                )}
              </div>
            )}

            {/* Espace Réactions & Mots Doux Section */}
            <div id="espace-mots-doux" className="max-w-2xl mx-auto w-full px-4 pt-10 pb-24 space-y-8 border-t border-theme-border mt-8">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center justify-center gap-2 text-theme-accent mb-1">
                  <Heart className="w-5 h-5 fill-theme-accent/20" />
                  <span className="text-xs font-mono uppercase tracking-widest">Espace Réactions & Mots Doux</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-serif text-theme-text">Laissez un mot après le visionnage</h3>
                <p className="text-xs text-theme-secondary max-w-md mx-auto">
                  Vous avez visionné la vidéo ? Laissez vos impressions, réactions ou un petit mot pour immortaliser ce souvenir.
                </p>
              </div>

              {/* Add Comment Form */}
              <form onSubmit={handleAddComment} className="bg-theme-card border border-theme-border p-6 space-y-4 shadow-xl">
                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-theme-secondary uppercase tracking-widest">Votre Prénom / Nom / Famille :</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Tata Sophie, Marc & Léa, Famille Dupont..."
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border p-3 text-sm text-theme-text outline-none focus:border-theme-accent font-sans"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-mono text-theme-secondary uppercase tracking-widest">Votre message ou réaction :</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Félicitations ! Le film est magnifique, quelles émotions..."
                    value={guestMessage}
                    onChange={(e) => setGuestMessage(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border p-3 text-sm text-theme-text outline-none focus:border-theme-accent font-sans resize-none"
                  />
                </div>

                <div className="flex items-center justify-between pt-2">
                  {commentSubmitted ? (
                    <span className="text-xs font-mono text-green-400 flex items-center gap-1.5">
                      <Check className="w-4 h-4" />
                      Merci ! Votre message a été publié.
                    </span>
                  ) : <span />}

                  <button
                    type="submit"
                    disabled={isSubmittingComment}
                    className="px-6 py-3 bg-theme-accent text-theme-bg font-semibold hover:opacity-90 transition-opacity font-mono text-xs uppercase tracking-widest cursor-pointer flex items-center gap-2 ml-auto shadow-md"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span>{isSubmittingComment ? 'Publication...' : 'Publier le mot'}</span>
                  </button>
                </div>
              </form>

              {/* List of Comments */}
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-theme-border pb-2">
                  <h4 className="text-xs font-mono uppercase tracking-widest text-theme-secondary flex items-center gap-2">
                    <MessageSquarePlus className="w-4 h-4 text-theme-accent" />
                    <span>Mots doux & Réactions ({localComments.length})</span>
                  </h4>
                </div>

                {localComments.length === 0 ? (
                  <p className="text-center text-xs font-mono text-theme-secondary py-8 border border-dashed border-theme-border/50">
                    Soyez le premier proche à laisser un petit mot après avoir regardé le film !
                  </p>
                ) : (
                  <div className="space-y-3">
                    {localComments.map((comment) => (
                      <motion.div
                        key={comment.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-theme-card/60 border border-theme-border space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-serif font-bold text-theme-text">{comment.authorName}</span>
                          <span className="text-[10px] font-mono text-theme-secondary">{comment.createdAt}</span>
                        </div>
                        <p className="text-xs text-theme-secondary leading-relaxed font-sans italic">
                          "{comment.message}"
                        </p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Share to Proches Modal */}
            <AnimatePresence>
              {showShareModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-theme-card border border-theme-border p-6 md:p-8 max-w-md w-full relative space-y-6 text-left shadow-2xl"
                  >
                    <button
                      onClick={() => setShowShareModal(false)}
                      className="absolute top-4 right-4 text-theme-secondary hover:text-theme-text p-2 cursor-pointer"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-theme-accent">
                        <Share2 className="w-5 h-5" />
                        <h3 className="text-lg font-serif text-theme-text">Partager avec vos proches</h3>
                      </div>
                      <p className="text-xs text-theme-secondary leading-relaxed">
                        Ce lien permet à votre famille et amis de visionner la vidéo en lecture seule. Ils <strong>ne pourront pas télécharger</strong> la vidéo et n'auront pas accès à votre compte client ni à vos paramètres.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-mono uppercase tracking-widest text-theme-secondary">Lien de lecture directe :</label>
                      <div className="flex items-center gap-2">
                        <input 
                          type="text" 
                          readOnly 
                          value={getGuestShareUrl()} 
                          className="w-full bg-theme-bg border border-theme-border p-3 text-xs font-mono text-theme-text outline-none truncate"
                        />
                        <button
                          onClick={handleCopyShareLink}
                          className="px-4 py-3 bg-theme-accent text-theme-bg hover:opacity-90 transition-opacity font-mono text-xs uppercase tracking-wider font-semibold flex items-center gap-2 shrink-0 cursor-pointer"
                        >
                          {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          <span>{copiedLink ? 'Copié !' : 'Copier'}</span>
                        </button>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-theme-border flex justify-end">
                      <button
                        onClick={() => setShowShareModal(false)}
                        className="px-5 py-2.5 bg-theme-bg border border-theme-border hover:border-theme-accent text-theme-text text-xs font-mono uppercase tracking-widest transition-colors cursor-pointer"
                      >
                        Fermer
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>

            {/* Download Help & In-App Browser Warning Modal */}
            <AnimatePresence>
              {showDownloadHelpModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-theme-card border border-theme-border p-6 md:p-8 max-w-lg w-full relative space-y-6 text-left shadow-2xl my-8"
                  >
                    <button
                      onClick={() => setShowDownloadHelpModal(false)}
                      className="absolute top-4 right-4 text-theme-secondary hover:text-theme-text p-2 cursor-pointer"
                    >
                      <X className="w-5 h-5" />
                    </button>

                    {/* In-App Browser Specific Alert Banner */}
                    {detectedInAppInfo?.isInAppBrowser ? (
                      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded space-y-2">
                        <div className="flex items-center gap-2 text-amber-400 font-bold text-xs font-mono uppercase tracking-wider">
                          <AlertCircle className="w-4 h-4 shrink-0" />
                          <span>Navigateur Intégré Détecté : {detectedInAppInfo.inAppName || 'Application Sociale'}</span>
                        </div>
                        <p className="text-xs text-amber-200/90 leading-relaxed font-sans">
                          Les applications comme <strong>LINE</strong>, <strong>Instagram</strong>, <strong>Facebook</strong> ou <strong>WeChat</strong> bloquent les téléchargements directs de vidéos et affichent souvent un écran blanc.
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full">
                          <Download className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-serif text-theme-text">Options de Téléchargement</h3>
                          <p className="text-xs text-theme-secondary font-mono">
                            {downloadModalVideo?.title || 'Film de Mariage'}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Step-by-Step Instructions */}
                    <div className="space-y-4 text-xs">
                      {detectedInAppInfo?.isInAppBrowser ? (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-theme-text">
                            👉 Pour télécharger votre vidéo sans page blanche :
                          </p>

                          <div className="p-3 bg-theme-bg border border-theme-border rounded space-y-1">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-theme-accent font-bold block">
                              Étape 1 : Ouvrir dans Safari / Chrome
                            </span>
                            <p className="text-xs text-theme-secondary">
                              Touchez l'icône <strong className="text-theme-text font-bold">⋯ ou ⋮</strong> en haut ou en bas de votre écran, puis sélectionnez <strong className="text-theme-text font-bold">« Ouvrir dans Safari »</strong> (sur iPhone) ou <strong className="text-theme-text font-bold">« Ouvrir dans Chrome »</strong> (sur Android).
                            </p>
                          </div>
                        </div>
                      ) : null}

                      {/* Direct Link Copy Option */}
                      <div className="space-y-2">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-theme-accent font-bold block">
                          {detectedInAppInfo?.isInAppBrowser ? 'Étape 2 : Copier le lien direct du fichier' : 'Lien direct du fichier vidéo :'}
                        </span>
                        <div className="flex items-center gap-2">
                          <input 
                            type="text" 
                            readOnly 
                            value={downloadModalVideo?.downloadUrl || downloadModalVideo?.videoUrl || activeVideo?.downloadUrl || activeVideo?.videoUrl || ''} 
                            className="w-full bg-theme-bg border border-theme-border p-3 text-xs font-mono text-theme-text outline-none truncate rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const directUrl = downloadModalVideo?.downloadUrl || downloadModalVideo?.videoUrl || activeVideo?.downloadUrl || activeVideo?.videoUrl || '';
                              navigator.clipboard.writeText(directUrl);
                              setCopiedDirectDownloadUrl(true);
                              setTimeout(() => setCopiedDirectDownloadUrl(false), 3000);
                            }}
                            className="px-4 py-3 bg-theme-accent text-theme-bg hover:opacity-90 transition-opacity font-mono text-xs uppercase tracking-wider font-semibold flex items-center gap-2 shrink-0 cursor-pointer rounded"
                          >
                            {copiedDirectDownloadUrl ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            <span>{copiedDirectDownloadUrl ? 'Copié !' : 'Copier'}</span>
                          </button>
                        </div>
                        <p className="text-[11px] text-theme-secondary italic">
                          Vous pouvez coller ce lien dans le navigateur de votre choix ou dans un gestionnaire de téléchargement.
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div className="pt-3 border-t border-theme-border space-y-2.5">
                        {/* Option 1: Stream Resilient Anti-Drop Proxy */}
                        <a
                          href={getResilientDownloadUrl(downloadModalVideo?.downloadUrl || downloadModalVideo?.videoUrl || activeVideo?.downloadUrl || activeVideo?.videoUrl || '', downloadModalVideo?.title || activeVideo?.title)}
                          download={`${(delivery.clientName || 'Film').replace(/[^a-zA-Z0-9_-]/g, '_')}_${(downloadModalVideo?.title || activeVideo?.title || 'Film_Mariage').replace(/[^a-zA-Z0-9_-]/g, '_')}.mp4`}
                          onClick={() => {
                            if (downloadModalVideo || activeVideo) {
                              sendDownloadLog(downloadModalVideo || activeVideo, 'completed', 'Stream anti-coupure lancé', 'video-master');
                            }
                          }}
                          className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs uppercase tracking-wider font-semibold rounded flex items-center justify-center gap-2 transition-colors cursor-pointer text-center shadow-md group"
                        >
                          <Zap className="w-4 h-4 text-emerald-200 group-hover:scale-110 transition-transform" />
                          <span>1. Téléchargement Anti-Coupure (Recommandé)</span>
                        </a>

                        {/* Option 2: Direct Source Link */}
                        <a
                          href={downloadModalVideo?.downloadUrl || downloadModalVideo?.videoUrl || activeVideo?.downloadUrl || activeVideo?.videoUrl || '#'}
                          target="_blank"
                          download
                          rel="noreferrer"
                          onClick={() => {
                            if (downloadModalVideo || activeVideo) {
                              sendDownloadLog(downloadModalVideo || activeVideo, 'completed', 'Lien source direct ouvert', 'video-master');
                            }
                          }}
                          className="w-full py-2.5 px-4 bg-theme-bg hover:bg-theme-card border border-theme-border text-theme-text font-mono text-xs uppercase tracking-wider font-semibold rounded flex items-center justify-center gap-2 transition-colors cursor-pointer text-center"
                        >
                          <Download className="w-4 h-4 text-theme-accent" />
                          <span>2. Lien Fichier Source Brut (Secours)</span>
                        </a>

                        {/* Light version download option if available */}
                        {downloadModalVideo?.lightVideoUrl && (
                          <a
                            href={downloadModalVideo.lightVideoUrl}
                            target="_blank"
                            download
                            rel="noreferrer"
                            onClick={() => {
                              if (downloadModalVideo) {
                                sendDownloadLog(downloadModalVideo, 'completed', 'Version légère téléchargée', 'video-light');
                              }
                            }}
                            className="w-full py-2.5 px-4 bg-theme-bg hover:bg-theme-card border border-amber-500/30 text-amber-300 font-mono text-xs uppercase tracking-wider font-semibold rounded flex items-center justify-center gap-2 transition-colors cursor-pointer text-center"
                          >
                            <Smartphone className="w-4 h-4 text-amber-400" />
                            <span>3. Télécharger Version 4G / Mobile (Légère & Rapide)</span>
                          </a>
                        )}

                        {/* Network tip */}
                        <div className="p-2 bg-emerald-950/30 border border-emerald-500/20 rounded text-[10px] font-mono text-emerald-200/90 leading-relaxed flex items-start gap-2">
                          <span className="text-emerald-400 font-bold">💡 Astuce PC :</span>
                          <span>Pour les gros fichiers (1.4 Go), si votre Wi-Fi saute, ouvrez la liste des téléchargements de votre navigateur (<strong>Ctrl+J</strong> sur Chrome/Edge) et cliquez sur <strong>« Reprendre »</strong>.</span>
                        </div>
                      </div>

                      {/* Multilingual Advice for international guests (e.g. Japan) */}
                      <div className="p-2.5 bg-black/40 border border-white/5 rounded text-[10px] font-mono text-theme-secondary space-y-1 leading-relaxed">
                        <div className="flex items-center gap-1.5 text-zinc-400 font-bold">
                          <Globe className="w-3.5 h-3.5 text-sky-400" />
                          <span>International & Japan notice (🇬🇧 / 🇯🇵)</span>
                        </div>
                        <p className="text-zinc-400">
                          <strong>EN :</strong> If you see a blank page, please open this link in Safari or Chrome instead of LINE or Instagram in-app browser.
                        </p>
                        <p className="text-zinc-400">
                          <strong>日本語 :</strong> 白い画面が表示される場合は、LINEやInstagram内ではなくSafariまたはChromeブラウザで開いてダウンロードしてください。
                        </p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-theme-border flex justify-end">
                      <button
                        type="button"
                        onClick={() => setShowDownloadHelpModal(false)}
                        className="px-5 py-2 bg-theme-bg border border-theme-border hover:border-theme-accent text-theme-text text-xs font-mono uppercase tracking-widest transition-colors cursor-pointer rounded"
                      >
                        Fermer
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>

          </motion.div>
        )}
      </main>
    </div>
  );
}
